import { useEffect, useMemo, useState } from 'react';
import type { AircraftType } from '../types';
import type { CustomerFollowUp } from '../services/customerApi';
import type { DashboardProps, TacticalEvent } from '../components/dashboard/types';
import type { AircraftFlightInfoMap, DashboardAirstripWeather } from './dashboard/types';
import { formatAgeMinutes, formatTime, isValidCoordinate } from './dashboard/dashboardUtils';
import { useDashboardPolling } from './dashboard/useDashboardPolling';
import { useDashboardMap } from './dashboard/useDashboardMap';
import { useDashboardTelemetry } from './dashboard/useDashboardTelemetry';
import { useDashboardReplay } from './dashboard/useDashboardReplay';

export const useDashboard = ({ aircrafts: initialAircrafts, quotes, premiumMode = false }: DashboardProps) => {
  const dark = premiumMode;

  const filteredInitialAircrafts = useMemo(
    () => initialAircrafts.filter((ac) => ac.tailNumber !== '5H-MAA'),
    [initialAircrafts]
  );

  const [liveAircrafts, setLiveAircrafts] = useState<AircraftType[]>(filteredInitialAircrafts);
  const [selectedAircraft, setSelectedAircraft] = useState<AircraftType | null>(null);
  const [myFollowUps, setMyFollowUps] = useState<(CustomerFollowUp & { customer: { id: string; name: string; company?: string; status: string } })[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [telemetryHistory, setTelemetryHistory] = useState<TacticalEvent[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [aircraftFlightInfo, setAircraftFlightInfo] = useState<AircraftFlightInfoMap>({});
  const [locationCache, setLocationCache] = useState<Record<string, string>>({});
  const [selectedAircraftLocation, setSelectedAircraftLocation] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'events' | 'history' | 'calculations'>('events');
  const [detailTrayMode, setDetailTrayMode] = useState<'docked' | 'full'>('docked');
  const [isDetailTrayExpanded, setIsDetailTrayExpanded] = useState(false);
  const [hoveredAirstrip, setHoveredAirstrip] = useState<string | null>(null);
  const [weatherData, setWeatherData] = useState<{ temp?: string; wind?: string; conditions?: string } | null>(null);

  const map = useDashboardMap({
    liveAircrafts,
    aircraftFlightInfo,
    isValidCoordinate,
  });

  const replay = useDashboardReplay({
    selectedAircraft: selectedAircraft ? { id: selectedAircraft.id, tailNumber: selectedAircraft.tailNumber } : null,
    activeTab,
    isDetailTrayExpanded,
    mapRef: map.mapRef,
    mapAirstrips: map.mapAirstrips,
  });

  useEffect(() => {
    if (!selectedAircraft?.tailNumber) return;
    const latest = liveAircrafts.find((ac) => ac.tailNumber === selectedAircraft.tailNumber);
    if (!latest) return;
    if (latest.id !== selectedAircraft.id || latest.telemetry?.lastUpdate !== selectedAircraft.telemetry?.lastUpdate) {
      setSelectedAircraft(latest);
    }
  }, [liveAircrafts, selectedAircraft]);

  useEffect(() => {
    if (selectedAircraft) {
      setDetailTrayMode((prev) => (prev === 'full' || prev === 'docked' ? prev : 'docked'));
    }
  }, [selectedAircraft]);

  useEffect(() => {
    setIsDetailTrayExpanded(detailTrayMode === 'full');
  }, [detailTrayMode]);

  useDashboardPolling({
    liveAircrafts,
    filteredInitialAircrafts,
    setLiveAircrafts,
    setMyFollowUps,
    setAircraftFlightInfo,
    setWeatherData,
  });

  useDashboardTelemetry({
    selectedAircraft,
    locationCache,
    mapRef: map.mapRef,
    isValidCoordinate,
    setTelemetryHistory,
    setLoadingHistory,
    setLocationCache,
    setSelectedAircraftLocation,
  });

  const projectedReplayEndpoints = useMemo(() => {
    const mapInstance = map.mapRef.current;
    if (!mapInstance || replay.historyReplayData.length < 2) return null;
    const first = replay.historyReplayData[0];
    const last = replay.historyReplayData[replay.historyReplayData.length - 1];
    if (!first?.lat || !first?.lng || !last?.lat || !last?.lng) return null;
    try {
      const s = mapInstance.project([first.lng, first.lat]);
      const e = mapInstance.project([last.lng, last.lat]);
      return { start: { x: s.x, y: s.y }, end: { x: e.x, y: e.y } };
    } catch {
      return null;
    }
  }, [map.mapRef, map.mapUpdateTrigger, replay.historyReplayData]);

  const projectedTrailArrows = useMemo((): { x: number; y: number; angle: number }[] => {
    const mapInstance = map.mapRef.current;
    if (!mapInstance || replay.historyReplayData.length < 4) return [];
    const pts = replay.historyReplayData.filter((ev: any) => ev.lat && ev.lng);
    if (pts.length < 4) return [];
    const arrows: { x: number; y: number; angle: number }[] = [];
    const MIN_PX = 90;
    let lastX = -9999;
    let lastY = -9999;
    try {
      for (let i = 1; i < pts.length - 1; i++) {
        const prev = pts[i - 1];
        const curr = pts[i];
        const pPrev = mapInstance.project([prev.lng, prev.lat]);
        const pCurr = mapInstance.project([curr.lng, curr.lat]);
        const dx = pCurr.x - lastX;
        const dy = pCurr.y - lastY;
        if (Math.sqrt(dx * dx + dy * dy) < MIN_PX) continue;
        const angle = Math.atan2(pCurr.y - pPrev.y, pCurr.x - pPrev.x) * 180 / Math.PI;
        arrows.push({ x: pCurr.x, y: pCurr.y, angle });
        lastX = pCurr.x;
        lastY = pCurr.y;
      }
    } catch {
      return [];
    }
    return arrows;
  }, [map.mapRef, map.mapUpdateTrigger, replay.historyReplayData]);

  const projectedReplayPos = useMemo(() => {
    const mapInstance = map.mapRef.current;
    if (!mapInstance || replay.historyReplayData.length === 0) return null;
    const ev = replay.historyReplayData[replay.replayIndex];
    if (!ev?.lat || !ev?.lng) return null;
    try {
      const p = mapInstance.project([ev.lng, ev.lat]);
      return { x: p.x, y: p.y, heading: ev.heading || 0 };
    } catch {
      return null;
    }
  }, [map.mapRef, map.mapUpdateTrigger, replay.historyReplayData, replay.replayIndex]);

  const todayStr = new Date().toISOString().split('T')[0];
  const stats = useMemo(() => {
    const todayQuotes = quotes.filter((q) => q.date === todayStr && (q.status === 'APPROVED' || q.status === 'SENT'));
    const todayBlockHours = todayQuotes.reduce((sum, q) => sum + (q.totalBlockTimeMinutes / 60), 0);
    const totalAircraft = liveAircrafts.length;
    const trackedAircraft = liveAircrafts.filter((a) => !!a.telemetry?.lastUpdate).length;
    const airborneNow = liveAircrafts.filter((a) => (a.telemetry?.groundSpeed || 0) > 50).length;
    const staleOrOffline = liveAircrafts.filter((a) => {
      const last = a.telemetry?.lastUpdate;
      if (!last) return true;
      const minutes = Math.floor((Date.now() - new Date(last).getTime()) / 60000);
      return minutes >= 15;
    }).length;
    const freshSignals = Math.max(0, trackedAircraft - staleOrOffline);

    return {
      totalAircraft,
      trackedAircraft,
      freshSignals,
      staleOrOffline,
      airborneNow,
      todayBlockHours,
    };
  }, [quotes, liveAircrafts, todayStr]);

  const airstripWeatherByCode = useMemo<Record<string, DashboardAirstripWeather>>(() => {
    if (!weatherData || (!weatherData.temp && !weatherData.wind && !weatherData.conditions)) return {};

    const tempMatch = weatherData.temp?.match(/-?\d+(\.\d+)?/);
    const windMatch = weatherData.wind?.match(/(VRB|\d{3})\/(\d{1,3})kt/i);
    const windDir = windMatch?.[1] && windMatch[1] !== 'VRB' ? parseInt(windMatch[1], 10) : undefined;
    const windSpeed = windMatch?.[2] ? parseInt(windMatch[2], 10) : undefined;
    const tempC = tempMatch?.[0] ? parseFloat(tempMatch[0]) : undefined;

    const base: DashboardAirstripWeather = {
      airstripCode: 'HTAR',
      sourceStationCode: 'HTKA',
      temperatureC: Number.isFinite(tempC as number) ? tempC : undefined,
      windDirectionDeg: Number.isFinite(windDir as number) ? windDir : undefined,
      windSpeedKt: Number.isFinite(windSpeed as number) ? windSpeed : undefined,
      visibility: undefined,
      condition: weatherData.conditions,
      metarAgeMinutes: undefined,
      source: 'METAR',
    };

    return {
      HTKA: { ...base, airstripCode: 'HTKA' },
      HTAR: { ...base, airstripCode: 'HTAR' },
    };
  }, [weatherData]);

  return {
    dark,
    liveAircrafts,
    selectedAircraft,
    myFollowUps,
    isFullscreen,
    searchQuery,
    telemetryHistory,
    loadingHistory,
    aircraftFlightInfo,
    locationCache,
    selectedAircraftLocation,
    activeTab,
    detailTrayMode,
    isDetailTrayExpanded,
    historyDateFrom: replay.historyDateFrom,
    historyDateTo: replay.historyDateTo,
    historyReplayData: replay.historyReplayData,
    isLoadingReplay: replay.isLoadingReplay,
    replayIndex: replay.replayIndex,
    isReplaying: replay.isReplaying,
    replaySpeed: replay.replaySpeed,
    mapLoaded: map.mapLoaded,
    mapAirstrips: map.mapAirstrips,
    mapUpdateTrigger: map.mapUpdateTrigger,
    hoveredAirstrip,
    weatherData,
    airstripWeatherByCode,
    currentReplayRowRef: replay.currentReplayRowRef,
    mapContainerRef: map.mapContainerRef,
    mapRef: map.mapRef,
    LEG_COLORS: replay.LEG_COLORS,
    projectedPositions: map.projectedPositions,
    projectedAirstrips: map.projectedAirstrips,
    projectedReplayEndpoints,
    projectedTrailArrows,
    projectedReplayPos,
    replayStats: replay.replayStats,
    replayRouteStops: replay.replayRouteStops,
    stats,
    formatAgeMinutes,
    formatTime,
    isValidCoordinate,
    getNearestAirstrip: replay.getNearestAirstrip,
    fetchHistoryReplay: replay.fetchHistoryReplay,
    setSelectedAircraft,
    setIsFullscreen,
    setSearchQuery,
    setActiveTab,
    setDetailTrayMode,
    setIsDetailTrayExpanded,
    setHistoryDateFrom: replay.setHistoryDateFrom,
    setHistoryDateTo: replay.setHistoryDateTo,
    setReplayIndex: replay.setReplayIndex,
    setIsReplaying: replay.setIsReplaying,
    setReplaySpeed: replay.setReplaySpeed,
    setHoveredAirstrip,
  };
};

export type DashboardModel = ReturnType<typeof useDashboard>;
